import {
    activityIdentitiesEqual,
    type ActivityIdentity,
    type ActivityIntent,
    type ActivityKey,
} from '../activity-journal';
import type { EligibleActivityToastDecision } from '../activity-journal/toast-policy';
import type { ActivityActionExecution } from './actions';

export type ActivityActionResult =
    | ActivityActionExecution
    | 'failed'
    | 'in-flight'
    | 'unavailable'
    | 'ignored';

export interface ActivityActionSnapshot {
    readonly identity: ActivityIdentity;
    readonly activeIdentityVersion: number;
    readonly intent: ActivityIntent;
    readonly version: string;
}

export type ActivityToastDecisionSnapshot = EligibleActivityToastDecision & {
    readonly identity: ActivityIdentity;
    readonly activeIdentityVersion: number;
    readonly buildingPublicId: string | null;
    readonly action: ActivityActionSnapshot | null;
};

export interface ActivityToastActionView {
    readonly label: string;
    execute(): Promise<ActivityActionResult>;
}

export interface ActivityToastView {
    readonly key: ActivityKey;
    readonly kind: 'error' | 'success' | 'warning';
    readonly action: ActivityToastActionView | null;
    readonly description: string | null;
}

export function activityActionSnapshotsEqual(
    current: ActivityActionSnapshot | null,
    expected: ActivityActionSnapshot
): boolean {
    return (
        activityActionSnapshotTargetMatches(current, expected) &&
        current.version === expected.version
    );
}

export function activityActionSnapshotTargetMatches(
    current: ActivityActionSnapshot | null,
    expected: ActivityActionSnapshot
): current is ActivityActionSnapshot {
    return (
        current !== null &&
        activityIdentitiesEqual(current.identity, expected.identity) &&
        current.activeIdentityVersion === expected.activeIdentityVersion &&
        activityIntentsEqual(current.intent, expected.intent)
    );
}

export function activityActionExecutionKey(
    key: ActivityKey,
    action: Pick<ActivityActionSnapshot, 'identity' | 'activeIdentityVersion'>
): string {
    return `${action.identity.userId}\u0000${action.identity.organisationId}\u0000${action.activeIdentityVersion}\u0000${key}`;
}

function activityIntentsEqual(
    left: ActivityIntent,
    right: ActivityIntent
): boolean {
    if (left.type !== right.type) return false;

    switch (left.type) {
        case 'view-receipt':
            return (
                right.type === 'view-receipt' &&
                left.buildingPublicId === right.buildingPublicId &&
                left.batchId === right.batchId
            );
        case 'open-preview':
            return (
                right.type === 'open-preview' &&
                left.buildingPublicId === right.buildingPublicId &&
                left.documentPublicId === right.documentPublicId
            );
        case 'download-selected-export':
        case 'download-export':
            return (
                right.type === left.type &&
                left.buildingPublicId === right.buildingPublicId &&
                left.jobId === right.jobId
            );
        case 'download-pack-export':
            return (
                right.type === 'download-pack-export' &&
                left.buildingPublicId === right.buildingPublicId &&
                left.packId === right.packId &&
                left.jobId === right.jobId
            );
    }
}
